import {
  addDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfMonth,
  format,
  parseISO,
  startOfWeek,
} from 'date-fns';
import { WEEK_STARTS_ON } from '@/constants/app.constants';
import { formatDate } from '@/utils/date';
import type {
  LeaveReportDates,
  LeaveReportDayKind,
  LeaveReportPeriod,
} from '../types/leaveReport.types';

const DAYS_IN_WEEK = 7;
const MONTH_LABEL_FORMAT = 'MMMM yyyy';
const SHORT_WEEKDAY_FORMAT = 'EEE';
const WEEK_OPTIONS = { weekStartsOn: WEEK_STARTS_ON } as const;

export interface CalendarDay {
  isoDate: string;
  dayOfMonth: number;
  isInMonth: boolean;
  isInPeriod: boolean;
}

export interface CalendarMonth {
  key: string;
  label: string;
  days: CalendarDay[];
}

export function getWeekdayLabels(): string[] {
  const weekStart = startOfWeek(new Date(), WEEK_OPTIONS);
  return Array.from({ length: DAYS_IN_WEEK }, (_, offset) =>
    format(addDays(weekStart, offset), SHORT_WEEKDAY_FORMAT),
  );
}

function getLastReportDate(period: LeaveReportPeriod): string {
  const today = formatDate(new Date());
  return period.dateTo < today ? period.dateTo : today;
}

export function getPeriodCalendarMonths(period: LeaveReportPeriod): CalendarMonth[] {
  const lastReportDate = getLastReportDate(period);
  if (lastReportDate < period.dateFrom) return [];

  const isInPeriod = (isoDate: string) => isoDate >= period.dateFrom && isoDate <= lastReportDate;

  return eachMonthOfInterval({
    start: parseISO(period.dateFrom),
    end: parseISO(lastReportDate),
  }).map((monthStart) => ({
    key: formatDate(monthStart),
    label: format(monthStart, MONTH_LABEL_FORMAT),
    days: eachDayOfInterval({
      start: startOfWeek(monthStart, WEEK_OPTIONS),
      end: endOfMonth(monthStart),
    }).map((day) => {
      const isoDate = formatDate(day);
      return {
        isoDate,
        dayOfMonth: day.getDate(),
        isInMonth: day >= monthStart,
        isInPeriod: isInPeriod(isoDate),
      };
    }),
  }));
}

export function getDayKindByDate(
  dates: LeaveReportDates,
  dayKinds: readonly LeaveReportDayKind[],
): Map<string, LeaveReportDayKind> {
  const dayKindByDate = new Map<string, LeaveReportDayKind>();
  for (const dayKind of dayKinds) {
    for (const isoDate of dates[dayKind]) {
      dayKindByDate.set(isoDate, dayKind);
    }
  }
  return dayKindByDate;
}
